import { api } from "./api";

export interface EditDppFields {
  content?: { name?: string; description?: string };
  removeMediaUrls?: string[];
  customerEmail?: string;
  department?: string;
  reference?: string;
  ticketAttachments?: { ticketId: string; attachmentId: string }[];
  mediaOrder?: string[];
}

export interface AddEventFields {
  type: string;
  title: string;
  description?: string;
  eventDate: string;
}

function dppPath(brand: string, model: string, serial: string): string {
  return `/admin/dpps/${encodeURIComponent(brand)}/${encodeURIComponent(
    model
  )}/${encodeURIComponent(serial)}`;
}

export interface EditDppResult {
  dppId: string;
  productId: string;
  content: Record<string, unknown>;
}

export async function editDpp(
  brand: string,
  model: string,
  serial: string,
  fields: EditDppFields,
  images: File[]
): Promise<EditDppResult> {
  const form = new FormData();
  form.append("payload", JSON.stringify(fields));
  for (const image of images) form.append("images", image);

  const { data } = await api.put<{
    data: EditDppResult;
  }>(dppPath(brand, model, serial), form);
  return data.data;
}

export async function transferDpp(
  brand: string,
  model: string,
  serial: string,
  toEmail: string,
  note?: string
): Promise<{ id: string; toEmail: string; status: string }> {
  const { data } = await api.post<{
    data: { id: string; toEmail: string; status: string };
  }>(`${dppPath(brand, model, serial)}/transfer`, { toEmail, note });
  return data.data;
}

export interface TicketAttachment {
  ticketId: string;
  ticketNumber: string;
  attachmentId: string;
  name: string;
  size: number;
  kind: "image" | "pdf";
}

export interface TicketAttachmentListing {
  billNumber: string | null;
  attachments: TicketAttachment[];
}

export async function listTicketAttachments(
  brand: string,
  model: string,
  serial: string
): Promise<TicketAttachmentListing> {
  const { data } = await api.get<{ data: TicketAttachmentListing }>(
    `${dppPath(brand, model, serial)}/ticket-attachments`
  );
  return data.data;
}

export async function fetchAttachmentThumbnail(
  brand: string,
  model: string,
  serial: string,
  ticketId: string,
  attachmentId: string
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `${dppPath(brand, model, serial)}/ticket-attachments/${encodeURIComponent(
      ticketId
    )}/${encodeURIComponent(attachmentId)}/thumbnail`,
    { responseType: "blob" }
  );
  return data;
}

export async function addEvent(
  brand: string,
  model: string,
  serial: string,
  fields: AddEventFields,
  eventImages: File[],
  documents: File[]
): Promise<{ success: boolean }> {
  const form = new FormData();
  form.append("payload", JSON.stringify(fields));
  for (const image of eventImages) form.append("eventImages", image);
  for (const doc of documents) form.append("documents", doc);

  const { data } = await api.post<{ data: { success: boolean } }>(
    `${dppPath(brand, model, serial)}/events`,
    form
  );
  return data.data;
}
